import { createSlice } from '@reduxjs/toolkit';
import {
  fetchProfilePicture,
  createProfilePicture,
  updateProfilePicture,
  deleteProfilePicture,
} from './userProfilePicturesThunks';

const initialState = {
  profilePictures: {}, // { userId: url|null|undefined }
  fetchedUsers: {}, // { userId: true }
  fetchedAt: {}, // { userId: timestamp } for cache expiry
  loading: false,
  error: null,
};

const userProfilePicturesSlice = createSlice({
  name: 'userProfilePictures',
  initialState,
  reducers: {
    batchFetchProfilePictures: (state, action) => {
      const userIds = action.payload;
      userIds.forEach((userId) => {
        if (!state.fetchedUsers[userId]) {
          state.profilePictures[userId] = undefined;
        }
      });
    },
    clearProfilePictureCache: (state, action) => {
      const { userId } = action.payload;
      delete state.profilePictures[userId];
      delete state.fetchedUsers[userId];
      delete state.fetchedAt[userId];
    },
  },
  extraReducers: (builder) => {
    builder
      // ------------------- FETCH -------------------
      .addCase(fetchProfilePicture.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        const userId = action.meta.arg;
        state.fetchedUsers[userId] = true;
      })
      .addCase(fetchProfilePicture.fulfilled, (state, action) => {
        state.loading = false;
        const { userId, pictureUrl } = action.payload;
        state.profilePictures[userId] = pictureUrl;
        state.fetchedUsers[userId] = true;
        state.fetchedAt[userId] = Date.now();
      })
      .addCase(fetchProfilePicture.rejected, (state, action) => {
        state.loading = false;
        const userId = action.payload?.userId || action.meta.arg;
        state.profilePictures[userId] = null; // explicitly no picture
        state.fetchedUsers[userId] = true;
        state.fetchedAt[userId] = Date.now();
        state.error = action.payload?.error || 'Failed to fetch profile picture';
      })

      // ------------------- CREATE -------------------
      .addCase(createProfilePicture.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProfilePicture.fulfilled, (state, action) => {
        state.loading = false;
        const { userId, pictureUrl } = action.payload;
        state.profilePictures[userId] = pictureUrl;
        state.fetchedUsers[userId] = true;
        state.fetchedAt[userId] = Date.now();
      })
      .addCase(createProfilePicture.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to create profile picture';
      })

      // ------------------- UPDATE -------------------
      .addCase(updateProfilePicture.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfilePicture.fulfilled, (state, action) => {
        state.loading = false;
        const { userId, pictureUrl } = action.payload;
        state.profilePictures[userId] = pictureUrl;
        state.fetchedUsers[userId] = true;
        state.fetchedAt[userId] = Date.now();
      })
      .addCase(updateProfilePicture.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to update profile picture';
      })

      // ------------------- DELETE -------------------
      .addCase(deleteProfilePicture.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProfilePicture.fulfilled, (state, action) => {
        state.loading = false;
        const { userId } = action.payload;
        state.profilePictures[userId] = null;
        state.fetchedUsers[userId] = true;
        state.fetchedAt[userId] = Date.now();
      })
      .addCase(deleteProfilePicture.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to delete profile picture';
      });
  },
});

export const { batchFetchProfilePictures, clearProfilePictureCache } =
  userProfilePicturesSlice.actions;
export default userProfilePicturesSlice.reducer;
